import { z } from 'zod'
import { logRequestFailure } from '@/helpers/request-outcome'
import { api } from '@/helpers/apiClient'
import { pageQuery } from '@/lib/pagination'

import {
  tenantDtoSchema,
  tenantFeatureChangeListDtoSchema,
  tenantListDtoSchema,
  type TenantDto,
  type TenantFeatureChangeListDto,
  type TenantListDto,
} from '@lib/dto/tenant.dto'
import type { TenantFeature } from '@lib/enums/tenant-feature.enum'
import { userDtoSchema, userListDtoSchema, type UserDto, type UserListDto } from '@lib/dto/user.dto'
import type { CreateTenantInput, UpdateTenantInput } from '@lib/input/tenant.input'
import type { InviteByEmailInput } from '@lib/input/user.input'

/** Service for the platform tenant-management endpoints (super admin only). */
class TenantService {
  async list(page?: number): Promise<TenantListDto> {
    try {
      return await api.get(`/tenants${pageQuery(page)}`, tenantListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list tenants', {}, error)
      throw error
    }
  }

  async get(id: string): Promise<TenantDto> {
    try {
      return await api.get(`/tenants/${id}`, tenantDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to get tenant', { id }, error)
      throw error
    }
  }

  async create(input: CreateTenantInput): Promise<TenantDto> {
    try {
      return await api.post('/tenants', input, tenantDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create tenant', {}, error)
      throw error
    }
  }

  async update(id: string, input: UpdateTenantInput): Promise<TenantDto> {
    try {
      return await api.patch(`/tenants/${id}`, input, tenantDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update tenant', {}, error)
      throw error
    }
  }

  async setFeature(tenantId: string, feature: TenantFeature, enabled: boolean): Promise<TenantDto> {
    try {
      return await api.put(`/tenants/${tenantId}/features/${feature}`, { enabled }, tenantDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to switch tenant feature', { tenantId, feature, enabled }, error)
      throw error
    }
  }

  async setBillingOwner(tenantId: string, userId: string): Promise<TenantDto> {
    try {
      return await api.put(`/tenants/${tenantId}/billing-owner`, { userId }, tenantDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to set tenant billing owner', { tenantId, userId }, error)
      throw error
    }
  }

  async setComp(tenantId: string, comped: boolean): Promise<TenantDto> {
    try {
      return await api.put(`/tenants/${tenantId}/comp`, { comped }, tenantDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to change tenant comp', { tenantId, comped }, error)
      throw error
    }
  }

  async extend(tenantId: string, what: 'trial' | 'grace', days: number): Promise<TenantDto> {
    try {
      return await api.post(`/tenants/${tenantId}/${what}-extensions`, { days }, tenantDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to extend tenant billing', { tenantId, what, days }, error)
      throw error
    }
  }

  async featureHistory(tenantId: string): Promise<TenantFeatureChangeListDto> {
    try {
      return await api.get(`/tenants/${tenantId}/feature-changes`, tenantFeatureChangeListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load tenant feature history', { tenantId }, error)
      throw error
    }
  }

  async listUsers(tenantId: string): Promise<UserListDto> {
    try {
      return await api.get(`/tenants/${tenantId}/users`, userListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list tenant users', { tenantId }, error)
      throw error
    }
  }

  async inviteUser(tenantId: string, input: InviteByEmailInput): Promise<UserDto> {
    try {
      return await api.post(`/tenants/${tenantId}/users`, input, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to invite tenant admin', { tenantId }, error)
      throw error
    }
  }

  async deletePendingUser(tenantId: string, userId: string): Promise<void> {
    try {
      await api.del(`/tenants/${tenantId}/users/${userId}`)
    } catch (error) {
      logRequestFailure('Failed to delete invited tenant user', { tenantId, userId }, error)
      throw error
    }
  }

  async deactivateUser(tenantId: string, userId: string): Promise<UserDto> {
    try {
      return await api.post(`/tenants/${tenantId}/users/${userId}/deactivate`, {}, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to deactivate tenant user', { tenantId, userId }, error)
      throw error
    }
  }

  async reactivateUser(tenantId: string, userId: string): Promise<UserDto> {
    try {
      return await api.post(`/tenants/${tenantId}/users/${userId}/reactivate`, {}, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to reactivate tenant user', { tenantId, userId }, error)
      throw error
    }
  }

  async resendInvite(tenantId: string, userId: string): Promise<void> {
    try {
      await api.post(`/tenants/${tenantId}/users/${userId}/resend-invite`, {}, z.null())
    } catch (error) {
      logRequestFailure('Failed to resend tenant invite', { tenantId, userId }, error)
      throw error
    }
  }
}

export const tenantService = new TenantService()
